// noinspection JSUnusedLocalSymbols

import {Backend} from "../index.js";
import {
	appConfig,
	account, accountUuid, accessTokenUuid,
	project, projectUuid,
	topic, topicUuid,
	outline, mergedOutline,
	annotation, annotationUuid,
	notification
} from "../../model/index.js";
import stores from "../../stores/index.js";

/**
 * This implements the interface but does nothing (useful for testing with fixtures).
 *
 * @todo Calls to these methods will cause "observable without reaction" warnings. Not sure if much can be done about it.
 */
class DummyBackend implements Backend {
	async callApi(): Promise<any> {
		throw new Error("Cannot use callApi in Dummy backend");
	}

	getAppConfig(): Promise<appConfig> {
		return Promise.resolve({});
	}

	acceptTimeTrackingTerms(projectUuid: projectUuid, reject: boolean | undefined): Promise<any> {
		return Promise.resolve(undefined);
	}

	changeAnnotationTopic(annotationUuid: annotationUuid, newTopicUuid: topicUuid, projectUuid: projectUuid): Promise<number> {
		return Promise.resolve(0);
	}

	checkOcrStatus(url: string): Promise<any> {
		return Promise.resolve(undefined);
	}

	convertOcr(url: string, pages: number, language: string, objectURL: string): Promise<any> {
		return Promise.resolve(undefined);
	}

	copyAnnotationToProject(annotationUuid: annotationUuid, newProjectUuid: projectUuid, newTopicUuid: topicUuid): Promise<number> {
		return Promise.resolve(0);
	}

	async createAnnotation(annotation: annotation, projectUuid: projectUuid, topicUuid: topicUuid | undefined, prepend: boolean): Promise<{position?: number; rendered_citation?: string}> {
		// TODO
		return {};
	}

	async createProject(project: project) {
	}

	async createTopic(topic: topic) {
	}

	deleteAnnotation(annotationUuid: annotationUuid): Promise<void> {
		return Promise.resolve(undefined);
	}

	event(data: {type: string; version: number; data: any; dt?: string; owner_uuid?: string}): Promise<any> {
		return Promise.resolve(undefined);
	}

	async getAnnotations(projectUuid: projectUuid): Promise<annotation[]> {
		return stores.annotation.byProject(projectUuid);
	}

	async getOutline(projectUuid: projectUuid): Promise<outline> {
		return stores.outline._outlines[projectUuid];
	}

	async getMergedOutline(projectUuid: projectUuid): Promise<mergedOutline> {
		return stores.outline.byProject(projectUuid);
	}

	async getProject(uuid: projectUuid): Promise<project> {
		const project = stores.project._projects[uuid];
		if (!project)
			throw new Error("Project not found: " + uuid);
		return project;
	}

	async getProjects(): Promise<project[]> {
		return stores.project.all;
	}

	getRenderedCitation(data: any): Promise<string> {
		return Promise.resolve("");
	}

	async getTopics(projectUuid: projectUuid): Promise<topic[]> {
		return stores.topic.byProject(projectUuid);
	}

	librarySearch(query: string, source: string): Promise<{results: {content: string; description: string}[]}> {
		return Promise.resolve({results: []});
	}

	logTimeOnPage(data: {project_uuid: projectUuid; duration_ms: number; end_dt: Date; page_url: string; clean_url?: string; activity?: Record<string, number>}): Promise<any> {
		return Promise.resolve(undefined);
	}

	async getAccount(uuid: accountUuid): Promise<account> {
		const account = stores.account._accounts[uuid];
		if (!account)
			throw new Error("Account not found: " + uuid);
		return account;
	}

	async updateAccount(uuid: accountUuid, data: any): Promise<{re_verification_email_sent: boolean}> {
		const account = stores.account._accounts[uuid];
		if (!account)
			throw new Error("Account not found: " + uuid);
		return {re_verification_email_sent: false};
	}

	login(data: {account_uuid: accountUuid; access_token: accessTokenUuid}): Promise<any> {
		return Promise.resolve(undefined);
	}

	sendFeedback(data: any): Promise<void> {
		return Promise.resolve(undefined);
	}

	updateAnnotation(annotationUuid: annotationUuid, data: any): Promise<void> {
		return Promise.resolve(undefined);
	}

	updatePreferences(data: any): Promise<any> {
		return Promise.resolve(undefined);
	}

	updateProjectSettings(projectUuid: any, data: any): Promise<void> {
		return Promise.resolve(undefined);
	}

	updateTopic(topicUUID: string, data: any): Promise<void> {
		return Promise.resolve(undefined);
	}

	uploadAnnotationImage(imgData: any, imgType: any, annotationUuid: any): Promise<any> {
		return Promise.resolve(undefined);
	}

	uploadAnnotationImageByUrl(imgUrl: any, annotationUuid: any): Promise<any> {
		return Promise.resolve(undefined);
	}

	async getNotifications(limit: number, includeDismissed: boolean): Promise<notification[]> {
		return stores.notification.all
			.filter(n => includeDismissed || n.dismissed_dt === undefined)
			.slice(0, limit);
	}

	async createTempAccount(clientToken: string, integration: string): Promise<{access_token: accessTokenUuid; account: account; project: project}> {
		throw new Error("Not implemented");
	}

	async integrationLogin(integration: string, uuid: accountUuid, email: string, password: string, projectUuid: projectUuid | null = null): Promise<{account_uuid: accountUuid; access_token: accessTokenUuid; project_uuid?: projectUuid}> {
		throw new Error("Not implemented");
	}

	async upgradeTempAccount(integration: string, uuid: accountUuid, name: string, email: string, password: string, terms: boolean): Promise<{account: account}> {
		throw new Error("Not implemented");
	}

	async forgotPassword(email: string): Promise<void> {
		throw new Error("Not implemented");
	}
}

export default DummyBackend;
